import {
  Backdrop,
  Box,
  CircularProgress,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  styled,
  Autocomplete,
  Button,
  TextField,
  DialogTitle,
  DialogContent,
  Dialog,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { SimpleCard } from "app/components";
import CustomButton from "app/components/Button";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback, useRef } from "react";
import {
  createDesignation,
  deleteDesignation,
  getAllDesignation,
  updateDesignation,
} from "app/services/Services";
import { generateErrorMessage, generateSuccessMessage } from "app/utils/GenerateMessage";
import { convertedDate } from "app/utils/utils";
import BackdropLoader from "app/components/BackdropLoader";
import { debounce } from "lodash";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmModal from "../employee-details/ConfirmModal";

const Container = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: { margin: "16px" },
  "& .breadcrumb": {
    marginBottom: "30px",
    [theme.breakpoints.down("sm")]: { marginBottom: "16px" },
  },
}));

const StyledTable = styled(Table)(() => ({
  whiteSpace: "pre",
  "& thead": {
    "& tr": { "& th": { paddingLeft: 0, paddingRight: 0 } },
  },
  "& tbody": {
    "& tr": { "& td": { paddingLeft: 0, textTransform: "capitalize" } },
  },
}));

const Designation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [designationsData, setDesignationsData] = useState([]);
  const [designationsDataCount, setDesignationsDataCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [sortColumn, setSortColumn] = useState("id");
  const [sortDirection, setSortDirection] = useState("desc");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const [selectedDesignation, setSelectedDesignation] = useState(null);
  const [formValues, setFormValues] = useState({
    designationName: "",
    designationValue: "",
  });

  // Create debounced function using useCallback and useRef
  const debouncedSearch = useRef(
    debounce((value) => {
      setDebouncedSearchText(value);
      setPage(0); // Reset to first page on new search
    }, 500)
  ).current;

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleChangePage = (_e, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const allDesignations = useCallback(() => {
    setDataLoading(true);
    
    const params = {
      page: `${page + 1}`,
      limit: `${rowsPerPage}`,
      search: debouncedSearchText,
      sortBy: sortColumn,
      sortOrder: sortDirection,
    };
    
    getAllDesignation(params)
      .then((res) => {
        setDataLoading(false);
        if (res.status === 200) {
          setDesignationsData(res?.data?.data || []);
          setDesignationsDataCount(res?.data?.designationsDataCount || 0);
        }
      })
      .catch((error) => {
        // console.error('Designation API Error:', error);
        setDataLoading(false);
        // generateErrorMessage(`${error?.response?.data?.message || "Failed to fetch designations"}`);
        setDesignationsData([]);
        setDesignationsDataCount(0);
      });
  }, [page, rowsPerPage, debouncedSearchText, sortColumn, sortDirection]);

  useEffect(() => {
    allDesignations();
  }, [allDesignations]);

  useEffect(() => {
    const { hostname } = window.location;
    document.title = `${hostname} | Designations `;
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    debouncedSearch(value);
  };

  const handleDeleteClick = (designation) => {
    setSelectedDesignation(designation);
    setConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDesignation) return;

    try {
      setLoading(true);
      await deleteDesignation(selectedDesignation.desId);
      generateSuccessMessage("Designation deleted successfully");
      await allDesignations();
      setConfirmModalOpen(false);
      setSelectedDesignation(null);
    } catch (err) {
      generateErrorMessage(
        err?.response?.data?.message || "Failed to delete designation"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedDesignation(null);
    setFormValues({
      designationName: "",
      designationValue: "",
    });
    setFormMode("add");
    setFormSubmitted(false);
    setDialogOpen(true);
  };

  const handleEdit = (designation) => {
    setSelectedDesignation(designation);
    setFormValues({
      designationName: designation.designationName,
      designationValue: designation.designationValue,
    });
    setFormMode("edit");
    setFormSubmitted(false);
    setDialogOpen(true);
  };

  const handleFormSubmit = () => {
    setFormSubmitted(true);

    const isValid =
      formValues.designationName.trim() && formValues.designationValue.trim();

    if (!isValid) return;

    const payload = {
      designationName: formValues.designationName.trim(),
      designationValue: formValues.designationValue.trim(),
    };

    setLoading(true);

    const request =
      formMode === "edit"
        ? updateDesignation(selectedDesignation.desId, payload)
        : createDesignation(payload);

    request
      .then((res) => {
        setDialogOpen(false);
        allDesignations();
        generateSuccessMessage(res?.data?.message || `Designation ${formMode === "edit" ? "updated" : "created"} successfully`);
      })
      .catch((err) => {
        generateErrorMessage(
          err?.response?.data?.message || "Operation failed"
        );
      })
      .finally(() => {
        setLoading(false);
        setFormSubmitted(false);
      });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setFormSubmitted(false);
  };

  return (
    <Container>
      <Paper elevation={2} sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <TextField
            label="Search Designation"
            variant="standard"
            size="small"
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            sx={{ width: 300 }}
            InputProps={{
              endAdornment: dataLoading ? (
                <CircularProgress size={20} />
              ) : null,
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleAdd}
            startIcon={<AddIcon />}
          >
            Add Designation
          </Button>
        </Box>

        <Box width="100%" overflow="auto">
          {designationsData?.length > 0 ? (
            <Box width="100%" overflow="auto">
              <TableContainer>
                <StyledTable sx={{ minWidth: 870 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Name</TableCell>
                      <TableCell align="center">Value</TableCell>
                      <TableCell align="center">Created Date</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {designationsData?.map((desig, index) => (
                      <TableRow key={`${desig?.desId || index}`}>
                        <TableCell align="center">
                          {desig?.designationName || "_"}
                        </TableCell>
                        <TableCell align="center">
                          {desig?.designationValue || "_"}
                        </TableCell>
                        <TableCell align="center">
                          {convertedDate(desig?.createdAt) || "_"}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={() => handleEdit(desig)}
                            color="primary"
                            disabled={dataLoading}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteClick(desig)}
                            color="error"
                            disabled={dataLoading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </StyledTable>
              </TableContainer>

              <TablePagination
                className="pagination-container"
                classes={{
                  select: "pagination-select",
                  actions: "pagination-actions",
                }}
                sx={{ px: 2 }}
                page={page}
                component="div"
                rowsPerPage={rowsPerPage}
                count={designationsDataCount}
                onPageChange={handleChangePage}
                rowsPerPageOptions={[10, 25, 50]}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          ) : (
            !dataLoading && (
              <p
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                No data available!
              </p>
            )
          )}
        </Box>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {formMode === "edit" ? "Edit Designation" : "Add Designation"}
        </DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Designation Name"
              variant="outlined"
              fullWidth
              value={formValues.designationName}
              onChange={(e) =>
                setFormValues({ ...formValues, designationName: e.target.value })
              }
              error={formSubmitted && !formValues.designationName.trim()}
              helperText={
                formSubmitted && !formValues.designationName.trim()
                  ? "Designation Name is required"
                  : ""
              }
            />

            <TextField
              label="Designation Value"
              variant="outlined"
              fullWidth
              value={formValues.designationValue}
              onChange={(e) =>
                setFormValues({
                  ...formValues,
                  designationValue: e.target.value,
                })
              }
              error={formSubmitted && !formValues.designationValue.trim()}
              helperText={
                formSubmitted && !formValues.designationValue.trim()
                  ? "Designation Value is required"
                  : ""
              }
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleFormSubmit}
            variant="contained"
            color="primary"
            disabled={
              !formValues.designationName.trim() ||
              !formValues.designationValue.trim()
            }
          >
            {formMode === "edit" ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Modal */}
      <ConfirmModal
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setSelectedDesignation(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Designation?"
        description="Are you sure you want to delete this designation? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        loading={loading}
      />

      <BackdropLoader loader={loading} />
    </Container>
  );
};

export default Designation;